/**
 * Artists bringing artists, and the money that rides on it.
 *
 * ₹1,000 to each side once the referred artist is real and their work has sold
 * ₹8,000 through the shop. Every rule below exists because the same rule
 * written loosely either costs money that was never earned or pays someone for
 * a transaction they manufactured.
 *
 * THE ECONOMICS, so nobody has to re-derive them. On ₹8,000 of orders we net
 * about ₹6,780 after GST, pay the artist ~₹1,130 in royalty, and keep ~₹5,650
 * before print costs. At a 50% print margin that is ~₹2,800 of gross profit
 * against a ₹2,000 payout. Thin, and deliberately so: this is acquisition
 * spend that only pays back if the referred artist keeps selling. At the
 * ₹4,000 threshold first proposed it was NEGATIVE — roughly −₹600 per
 * qualification — which is why the number is 8,000.
 *
 * COMMISSIONS DO NOT COUNT, and that is the most important line here. A
 * commission is a direct arrangement between two people with us in the middle.
 * If a referrer commissions the artist they referred for ₹8,000, they have
 * manufactured the threshold between themselves, taken most of the money back
 * as the designer's fee, and collected ₹2,000 from us — and every step of it
 * looks legitimate in the data. Only shop orders from unconnected buyers count.
 */
/** ₹1,000 to each side. Cash, not credits — an artist is somebody we pay. */
export const REFERRAL_BONUS_MINOR = 100_000;
/** Net delivered order value the referred artist's work must reach. */
export const REFERRAL_QUALIFY_MINOR = 800_000;
/** From the referred artist's signup. Caps how long the liability stays open. */
export const REFERRAL_QUALIFY_WINDOW_DAYS = 90;
/**
 * After the threshold is crossed, before money moves.
 *
 * The return window has to close first. Paying on delivery and clawing back a
 * refund later means chasing money out of somebody's bank account, which we
 * will not win and should not attempt.
 */
export const REFERRAL_HOLD_DAYS = 30;
/** Per referrer, per financial year. Uncapped is unbounded liability. */
export const REFERRAL_MAX_PER_YEAR = 10;
/** Artworks a profile must have published before it counts as complete. */
export const REFERRAL_MIN_ARTWORKS = 3;
/** Enough that "about" is a sentence about them, not a placeholder. */
export const REFERRAL_MIN_ABOUT_CHARS = 120;
const DAY_MS = 24 * 60 * 60 * 1000;
/* ------------------------------------------------------------------ *
 * Profile completeness
 * ------------------------------------------------------------------ */
/**
 * Returns what is MISSING rather than a boolean.
 *
 * An artist told "your profile is incomplete" with no list will email us to
 * ask which part, and that email costs more than the field ever did.
 */
export function referralProfileGaps(designer, publishedArtworkCount) {
    const missing = [];
    if (!designer.displayName?.trim())
        missing.push('your name');
    if (!designer.handle?.trim())
        missing.push('a handle');
    if ((designer.about?.trim().length ?? 0) < REFERRAL_MIN_ABOUT_CHARS) {
        missing.push(`an about of at least ${REFERRAL_MIN_ABOUT_CHARS} characters`);
    }
    if (!designer.avatarMediaId)
        missing.push('a picture of you');
    if (!designer.coverMediaId)
        missing.push('a cover image');
    if (!designer.pan)
        missing.push('your PAN');
    if (!designer.payout?.accountNumber)
        missing.push('your payout details');
    if (publishedArtworkCount < REFERRAL_MIN_ARTWORKS) {
        const short = REFERRAL_MIN_ARTWORKS - publishedArtworkCount;
        missing.push(`${short} more published ${short === 1 ? 'design' : 'designs'}`);
    }
    return missing;
}
/**
 * Whether one order may be counted, and why not when it may not.
 *
 * The reason string is for the admin screen: "£X of orders, 3 excluded" with no
 * explanation is exactly the kind of number that generates a dispute.
 */
export function orderCountsToward(order, parties, windowStart, windowEnd) {
    if (order.isCommission)
        return { counts: false, reason: 'commissioned work does not count' };
    if (order.refundedAt)
        return { counts: false, reason: 'refunded' };
    if (order.status !== 'DELIVERED')
        return { counts: false, reason: 'not delivered yet' };
    if (!order.deliveredAt)
        return { counts: false, reason: 'no delivery date' };
    if (order.deliveredAt < windowStart || order.deliveredAt > windowEnd) {
        return { counts: false, reason: 'outside the 90-day window' };
    }
    if (order.buyerUid && order.buyerUid === parties.referrerUid) {
        return { counts: false, reason: 'bought by the referrer' };
    }
    if (order.buyerUid && order.buyerUid === parties.referredUid) {
        return { counts: false, reason: 'bought by the artist themselves' };
    }
    return { counts: true };
}
/**
 * Adds up what counts, and records WHEN the threshold was crossed.
 *
 * The crossing date is not decoration: the 30-day hold runs from it, so it has
 * to be the delivery date of the order that took the total over ₹8,000 rather
 * than "now". Computing it from `now` would restart the hold on every
 * recalculation and the bonus would never become payable.
 */
export function qualifyingTotal(orders, parties, windowStart, windowEnd) {
    const counted = [...orders]
        .map((o) => ({ order: o, verdict: orderCountsToward(o, parties, windowStart, windowEnd) }))
        .filter((r) => r.verdict.counts)
        // Chronological, so the crossing order is the one that actually crossed it.
        .sort((a, b) => (a.order.deliveredAt ?? 0) - (b.order.deliveredAt ?? 0));
    let running = 0;
    let crossedAt;
    for (const { order } of counted) {
        running += order.grossMinor;
        if (crossedAt === undefined && running >= REFERRAL_QUALIFY_MINOR) {
            crossedAt = order.deliveredAt;
        }
    }
    return {
        totalMinor: running,
        countedOrderIds: counted.map((c) => c.order.orderId),
        excluded: orders
            .map((o) => ({ o, v: orderCountsToward(o, parties, windowStart, windowEnd) }))
            .filter((r) => !r.v.counts)
            .map((r) => ({ orderId: r.o.orderId, reason: r.v.reason ?? 'excluded' })),
        ...(crossedAt !== undefined ? { crossedAt } : {}),
    };
}
/* ------------------------------------------------------------------ *
 * State
 * ------------------------------------------------------------------ */
export function referralExpiryAt(signedUpAt) {
    return signedUpAt + REFERRAL_QUALIFY_WINDOW_DAYS * DAY_MS;
}
export function referralPayableAt(crossedAt) {
    return crossedAt + REFERRAL_HOLD_DAYS * DAY_MS;
}
/** Indian financial year, for the per-referrer cap. 1 Apr – 31 Mar. */
export function financialYearOf(at) {
    const ist = new Date(at + 5.5 * 3600_000);
    const y = ist.getUTCFullYear();
    const startYear = ist.getUTCMonth() >= 3 ? y : y - 1;
    return `${startYear}-${String((startYear + 1) % 100).padStart(2, '0')}`;
}
/**
 * The whole decision, in one pure function.
 *
 * Deliberately takes everything it needs rather than reading anything: this is
 * the rule that moves ₹2,000, and it should be possible to prove it correct
 * without a database.
 */
export function evaluateReferral(input) {
    const { createdAt, now, total, profileGaps } = input;
    // Suspension voids it on either side, whatever else is true. An account we
    // have stopped trusting does not get paid for introducing another one.
    if (!input.referrerActive)
        return { status: 'VOID', reason: 'the referring artist is not active' };
    if (!input.referredActive)
        return { status: 'VOID', reason: 'the referred artist is not active' };
    if (input.referrerPaidThisYear >= REFERRAL_MAX_PER_YEAR) {
        return { status: 'VOID', reason: `the ${REFERRAL_MAX_PER_YEAR}-a-year limit is reached` };
    }
    const crossed = total.crossedAt;
    if (crossed === undefined) {
        // Only expire once the threshold has NOT been met. An artist who crossed it
        // on day 89 must not lose the bonus because an admin paid it on day 91.
        if (now > referralExpiryAt(createdAt)) {
            return { status: 'VOID', reason: 'the 90-day window closed before ₹8,000 of orders' };
        }
        return { status: 'PENDING' };
    }
    if (profileGaps.length > 0) {
        return { status: 'PENDING', reason: `profile still needs ${profileGaps.join(', ')}` };
    }
    const payableAt = referralPayableAt(crossed);
    if (now < payableAt) {
        return { status: 'HELD', qualifiedAt: crossed, payableAt };
    }
    return { status: 'PAYABLE', qualifiedAt: crossed, payableAt };
}
//# sourceMappingURL=artistReferral.js.map